import { JSX } from 'react';
import WindowService from '@src/services/window.service';

import Faq from '@src/pages/landing/components/faq';
import { PhoneNumberLink } from '@src/components/phone-number-link';
import {
  chewyCarePlusSupport,
  trupanionCarePlusSupport,
} from '@src/constants/phone-numbers';

export default function Faqs({
  onSectionView,
}: {
  onSectionView: (
    label: string,
    value: string,
    extraData?: Record<string, string>,
  ) => void;
}): JSX.Element {
  return (
    <section id="pi-careplus__faqs" className="pi-careplus__faqs">
      <div className="pi-careplus__faqs--content">
        <h1 className="pi-careplus__faqs--title">Frequently Asked Questions</h1>
        <Faq
          title="CarePlus, Chewy and Our Partners"
          analytics={{
            label: 'CarePlusChewyandOurPartners',
            value: 'CarePlus, Chewy and Our Partners',
          }}
          onSectionView={onSectionView}
          descendants={[
            {
              title: 'What is CarePlus by Chewy?',
              text: `CarePlus is Chewy's wellness and insurance brand. It offers a unique selection of plans from trusted partners that are specifically designed for Chewy customers to deliver a pet-first experience. With these plans, you can provide your pet with wellness preventative care, accident & illness coverage, or combine them for comprehensive protection. At Chewy, we are focused on the wellbeing of your pet, and CarePlus gives you the ability to provide your pet with the best possible care during all of life's moments.`,
            },
            {
              title: 'What are CarePlus wellness and insurance plans?',
              text: 'CarePlus offers a unique selection of pet wellness and insurance plans, which you can buy standalone or together (coverage by state may vary).',
              descendants: [
                {
                  text: (
                    <>
                      <u>Insurance plans</u> are designed to help in the event
                      of accidents and illnesses. These plans cover new,
                      unexpected healthcare needs from accidents to illnesses.
                      Coverage includes surgeries, X-rays, medications, fees
                      related to hospitalizations, and more. To make sure you
                      get the most out of your coverage, it&apos;s best to
                      enroll in a pet insurance plan when your pet is young and
                      healthy. If they get injured or sick before they are
                      covered, treatments related to these issues would not be
                      covered, since those conditions would be considered
                      pre-existing.
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      <u>Wellness plans</u> are affordable, annual packages
                      designed to cover preventative care for your pet. These
                      plans help you cover your pet&apos;s annual veterinary
                      exam fee, vaccines, lab tests (e.g., annual heartworm
                      test), dental cleaning, spay neutering, and flea, tick,
                      and heartworm medications.
                    </>
                  ),
                },
              ],
            },
            {
              title: `What is Trupanion and Lemonade's role in my coverage? I purchased my plan on Chewy.com but see Trupanion or Lemonade listed on my policy.`,
              text: 'We partnered with Trupanion and Lemonade to offer the best pet insurance and wellness plans on the market exclusively for Chewy customers.  We want you to understand the parties involved in offering these plans for you and your pet.',
              descendants: [
                {
                  text: (
                    <>
                      <strong>Chewy</strong> is us. We are offering these
                      wellness and insurance plans through Chewy Insurance
                      Services, LLC., our licensed insurance agency.
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      <strong>CarePlus</strong> is Chewy&apos;s pet wellness and
                      insurance brand that has curated these plans for you.
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      <strong>Trupanion</strong> is one of our program partners
                      and an affiliate of the American Pet Insurance Company
                      (APIC). Trupanion administers the policy and manages
                      claims on APIC&apos;s behalf. Once you enroll in an
                      insurance or wellness plan, you will begin to receive
                      communications from Trupanion regarding your plan(s).
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      <strong>Lemonade</strong> is one of our program partners,
                      and a certified B Corp and Public Benefit Corporation.
                      Lemonade administers the policy and manages claims. Once
                      you enroll in an insurance plan, you will begin to receive
                      communications from Lemonade regarding your plan(s).
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      <strong>American Pet Insurance Company</strong> is the
                      underwriter for Trupanion that issues the wellness and
                      insurance plans and is responsible for collecting your
                      monthly premium and paying customer claims.
                    </>
                  ),
                },
              ],
            },
          ]}
        />
        <Faq
          title="Plans and Coverage"
          analytics={{
            label: 'PlansandCoverage',
            value: 'Plans and Coverage',
          }}
          onSectionView={onSectionView}
          descendants={[
            {
              title: 'Can I use my CarePlus plans with any veterinarian?',
              text: `Yes. Our policies are flexible and can be used at any licensed veterinarian throughout the U.S.; Trupanion policies can also be used in Puerto Rico, Canada and Australia.`,
            },
            {
              title: 'What are pre-existing conditions? Are they covered?',
              descendants: [
                {
                  text: 'Waiting periods and pre-existing condition exclusions do apply to insurance plans. Any injury or illness that your pet showed signs of before you enrolled in a pet insurance plan or that develops before your waiting period ends is considered a pre-existing condition. It is best to enroll in a pet insurance plan as soon as you bring your pet home to ensure they are covered from unexpected accidents and illnesses, no matter when they occur.',
                },
                {
                  text: `For Trupanion insurance plans, after your waiting period ends, they will review your pet's medical history and provide you with details on any past conditions that may impact your pet's insurance coverage. You will receive this medical record summary once your insurance coverage begins (does not apply to Accident Only plans).`,
                },
                {
                  text: `For Lemonade insurance plans, Lemonade will review your pet's medical history and determine what will be considered pre-existing conditions. Pre-existing conditions do not determine your eligibility for coverage, or your premium price, they simply determine what Lemonade is able to cover as a part of your plan.`,
                },
                {
                  text: 'You do not have to worry about pre-existing conditions for wellness plans, which focus on future, proactive pet healthcare.',
                },
              ],
            },
            {
              title: `Is there a waiting period before my pet's coverage begins?*`,
              text: 'CarePlus offers pet insurance and wellness plans provided by Trupanion or Lemonade: the waiting period for your coverage will depend on which of these providers your plan is from. Trupanion waiting periods begin from the day you purchased your plan. Lemonade waiting periods begin on the policy effective date (00:01am the day after you purchase your policy).',
              descendants: [
                {
                  text: 'Wellness plans: there are no waiting periods.',
                },
                {
                  text: `Insurance plans: coverage starts after a short waiting period, which is determined by the type of plan you select (see details in the table below).`,
                },
              ],
              table: (
                <>
                  <thead>
                    <tr>
                      <th scope="row">Reason for vet visit:</th>
                      <th scope="col">Accident Only</th>
                      <th scope="col">Value</th>
                      <th scope="col">Essential</th>
                      <th scope="col">Essential Plus</th>
                      <th scope="col">Complete</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row">Accident</th>
                      <td>5 days</td>
                      <td>2 days</td>
                      <td>5 days</td>
                      <td>5 days</td>
                      <td>5 days</td>
                    </tr>
                    <tr>
                      <th scope="row">Illness</th>
                      <td>n/a</td>
                      <td>14 days</td>
                      <td>14 days</td>
                      <td>14 days</td>
                      <td>14 days</td>
                    </tr>
                    <tr>
                      <th scope="row">Cruciate Knee Injury</th>
                      <td>30 days</td>
                      <td>6 months</td>
                      <td>14 days</td>
                      <td>14 days</td>
                      <td>14 days</td>
                    </tr>
                  </tbody>
                </>
              ),
              footer: <>* Waiting periods vary by state.</>,
            },
            {
              title:
                'How does pet insurance differ from my own (or human) health insurance?',
              text: 'Human and pet insurance are similar in that they both offer the insured financial coverage to help protect against the cost of unexpected medical care. However, we want you to know three important differences:',
              descendants: [
                {
                  text: (
                    <>
                      <u>Pet insurance works at any veterinarian clinic</u>: Pet
                      insurance is not restricted by specific networks. Your pet
                      can go to the nearest licensed veterinarian, animal
                      hospital, or licensed specialist and they will be covered.
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      <u>
                        Pet insurance does not typically cover preventative care
                      </u>
                      : Pet insurance does not typically cover routine or
                      preventative care. Many pet parents purchase an additional
                      wellness plan to complement pet insurance for
                      comprehensive coverage.
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      <u>
                        Pet insurance does not cover pre-existing conditions
                      </u>
                      : Pre-existing conditions are generally not covered by pet
                      insurance. It is recommended to enroll in a pet insurance
                      plan when your pet is young and healthy to ensure they are
                      covered for unexpected accidents and illnesses, no matter
                      when they occur.
                    </>
                  ),
                },
              ],
            },
          ]}
        />
        <Faq
          title="Managing a CarePlus Plan"
          analytics={{
            label: 'ManagingaCarePlusPlan',
            value: 'Managing a CarePlus Plan',
          }}
          onSectionView={onSectionView}
          descendants={[
            {
              title: 'How do I manage my policy?',
              text: 'CarePlus offers pet wellness and insurance plans provided by Trupanion or Lemonade: where you manage your policy will depend on which of these providers your plan is from.',
              descendants: [
                {
                  text: (
                    <>
                      Trupanion: via the Pet Insurance dashboard at{' '}
                      <a
                        href="https://www.chewy.com/pet-insurance/policies/dashboard"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        https://www.chewy.com/pet-insurance/policies/dashboard
                      </a>
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      Lemonade: via the{' '}
                      <a
                        href="https://www.lemonade.com/download"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Lemonade app
                      </a>
                      . After you purchase your policy, you&apos;ll get a unique
                      link to download the app.
                    </>
                  ),
                },
              ],
            },
            {
              title: 'How do I file a claim?',
              text: 'CarePlus offers pet wellness and insurance plans provided by Trupanion or Lemonade: the way you file a claim will depend on which of these providers your plan is from.',
              descendants: [
                {
                  suffix: (
                    <>
                      <u>Trupanion</u>: Trupanion has proprietary software which
                      allows for payments directly to your veterinarian. If your
                      veterinarian is set up to accept payments from Trupanion,
                      you just pay your portion of the veterinary invoice at
                      checkout, and Trupanion can often take care of the rest
                      within minutes. If your veterinarian is not yet set up to
                      receive direct payments from Trupanion, you can visit the
                      Pet Insurance dashboard at{' '}
                      <a
                        href="https://www.chewy.com/pet-insurance/policies/dashboard"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        https://www.chewy.com/pet-insurance/policies/dashboard
                      </a>{' '}
                      after you have purchased your plan to learn how your
                      veterinarian clinic can get set up to receive payments
                      directly from Trupanion. If your veterinarian is not set
                      up to accept payments from Trupanion or you prefer to pay
                      the full invoice out of pocket, Trupanion can reimburse
                      you quickly. All Trupanion needs to process your claim is
                      a copy of your itemized invoice and a few administrative
                      details about your claim, which you can submit in two
                      ways:
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      <strong>By member portal</strong>: Submit a digital claim
                      through the Pet Insurance dashboard at{' '}
                      <a
                        href="https://www.chewy.com/pet-insurance/policies/dashboard"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        https://www.chewy.com/pet-insurance/policies/dashboard
                      </a>
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      <strong>By mail</strong>: Send a copy of your itemized
                      invoice to Trupanion directly at: Trupanion, 6000 4th
                      Avenue South, Suite 200, Seattle, WA 98108.
                    </>
                  ),
                },
                {
                  suffix: (
                    <>
                      <u>Lemonade</u>: You can view details of your policy via
                      the Pet Insurance dashboard at{' '}
                      <a
                        href="https://www.chewy.com/pet-insurance/policies/dashboard"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        https://www.chewy.com/pet-insurance/policies/dashboard
                      </a>
                      , and manage your policy via the{' '}
                      <a
                        href="https://www.lemonade.com/download"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Lemonade app
                      </a>
                      . After you purchase your policy, you&apos;ll get a unique
                      link to download the app. You can file a claim any time
                      from your phone through the Lemonade app by clicking the
                      &apos;File a Claim&apos; button. To process your claim,
                      all Lemonade needs is your invoice which you can then
                      itemize by typing into the chat. Lemonade settles around
                      half of claims instantly and funds are wired directly to
                      your account.
                    </>
                  ),
                },
              ],
            },
            {
              title: 'What is the cancellation policy?',
              text: (
                <>
                  CarePlus offers pet wellness and insurance plans provided by
                  Trupanion or Lemonade: your cancellation options will depend
                  on which of these providers your plan is from. Cancellation
                  requirements may vary by state; please refer to your policy
                  documents for full details and contact us at{' '}
                  <PhoneNumberLink contact={chewyCarePlusSupport} /> with any
                  questions.
                  <br />
                  <br />
                  <u>Trupanion wellness plan cancellation</u>: You can cancel
                  your wellness plan at any time, and your cancellation is
                  effective the day Trupanion receives your notice. Trupanion
                  will prorate a refund of your monthly cost, if one is due, as
                  explained in detail below. If you cancel your wellness plan
                  within the first 30 days following your enrollment date and
                  Trupanion has not paid any claims for your pet, Trupanion will
                  refund 100% of your monthly cost. If Trupanion paid claims for
                  your pet during the first 30-days, you can still cancel
                  anytime, but an additional payment may apply. If the amount
                  you paid to Trupanion for your wellness plan during your
                  current plan term exceeds what Trupanion paid you in claims,
                  Trupanion will refund a prorated portion of your monthly
                  costs. If the amount Trupanion paid you in claims exceeds the
                  amount you paid to Trupanion for your wellness plan during
                  your current plan term, you will need to pay Trupanion the
                  difference (not to exceed 12 months&apos; worth of your
                  monthly costs). For example, if you would like to cancel your
                  wellness plan three months into your plan term and your
                  monthly cost is $25, then you have paid Trupanion $75 for your
                  wellness plan. If Trupanion paid you $100 in claims payments,
                  then you would need to pay Trupanion $25 to cancel your
                  wellness plan before the end of the full year.
                  <br />
                  <br />
                  <u>Trupanion insurance cancellation</u>: You can cancel your
                  pet insurance plan at any time by calling, emailing, or
                  mailing a letter to Trupanion directly. The cancellation is
                  effective the day Trupanion receives your notice, and you can
                  expect to receive a prorated refund of your current monthly
                  cost if one is due. If you cancel your plan within the first
                  30 days following your enrollment date, Trupanion will refund
                  100% of your monthly cost if they have not yet paid any claims
                  for your pet.
                </>
              ),
              footer: (
                <>
                  <u>Lemonade wellness and insurance plans cancellation</u>: You
                  can cancel your policy at any time through the Lemonade app,
                  or by emailing{' '}
                  <a href="mailto:help@lemonade.com">help@lemonade.com</a>. If
                  you cancel within the first 30 days, you&apos;ll get a full
                  refund as long as you haven&apos;t filed any claims.
                  Otherwise, if you cancel, you&apos;ll receive a refund for the
                  remaining period you&apos;ve paid for. Cancellation
                  requirements may vary by state; please refer to your policy
                  documents for full details, and contact us at{' '}
                  <PhoneNumberLink contact={chewyCarePlusSupport} /> with any
                  questions.
                </>
              ),
              descendants: [
                {
                  text: (
                    <>
                      Phone:{' '}
                      <PhoneNumberLink contact={trupanionCarePlusSupport} />
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      Email:{' '}
                      <a
                        href="mailto:care@trupanion.com"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        care@trupanion.com
                      </a>
                    </>
                  ),
                },
                {
                  text: (
                    <>
                      Trupanion, 6000 4th Avenue South, Suite 200, Seattle, WA
                      98108
                    </>
                  ),
                },
              ],
            },
            {
              title: 'Do I need a smartphone to manage my plan?',
              text: 'CarePlus offers pet wellness and insurance plans provided by Trupanion or Lemonade: for plans from Lemonade, you need a smartphone in order to access the Lemonade mobile app, which is where you will manage your policy and submit claims. You do not need one to manage Trupanion plans.',
            },
          ]}
        />
        <Faq
          title="Purchasing a Policy"
          analytics={{
            label: 'PurchasingaPolicy',
            value: 'Purchasing a Policy',
          }}
          onSectionView={onSectionView}
          descendants={[
            {
              title: 'Are discounts available when I purchase a CarePlus plan?',
              text: 'Yes, which partner you choose determines the type of discounts available:',
              descendants: [
                {
                  text: `Trupanion multipet: if you insure 2 or more pets with a Trupanion plan, you'll receive a 10% discount on your monthly premium for all pets (inclusive of wellness and Insurance plans).`,
                },
                {
                  text: `Lemonade multipet: if you insure 2 or more pets with a Lemonade plan, you'll receive a 5% discount on your monthly premium for all pets (the discount only applies to the Accident & Illness portion of your policy).`,
                },
                {
                  text: `Lemonade bundling discount:  If you’re also covered with Lemonade homeowners, renters, term life, or car insurance, you’ll receive a 10% discount on each policy.`,
                },
                {
                  text: 'Lemonade annual discount: if you choose to pay for a whole year of Lemonade pet insurance upfront, you will receive a 5% discount.',
                },
              ],
            },
            {
              title:
                'Can I get a wellness plan from Trupanion and an insurance plan from Lemonade for the same pet (and vice versa)?',
              text: 'No, if you want to bundle a wellness plan with an Insurance plan for the same pet, you have to have both plans from either Trupanion or Lemonade. If you have more than one pet, you can select to have a Trupanion plan for one pet and a Lemonade plan for the other.',
            },
          ]}
        />
      </div>
    </section>
  );
}
